import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "./layout";
import { useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { doFetch } from "../redux/api/actions";
import { GET_TOUR } from "../redux/api/constants";
import { apiAddress } from "../config";
import { loadTourTracks, playTrackRequest, setCurrentTour } from "../redux/audio/actions";

const Tour = () => {
    const { tourId } = useParams();
    const tour = useSelector(state => state.API.tour);
    const accessToken = useSelector(state => state.Auth.accessToken);
    const dispatch = useDispatch();

    const [tourData, setTourData] = useState(null);
    const [tourLoading, setTourLoading] = useState(false);
    const [tourError, setTourError] = useState(null);

    useEffect(() => {
        dispatch(doFetch(GET_TOUR, `tours/${tourId}`, null));
    }, [tourId, dispatch]);

    useEffect(() => {
        let isCancelled = false;
        const load = async () => {
            setTourLoading(true);
            setTourError(null);
            try {
                const headers = {};
                if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
                const res = await fetch(`${apiAddress}/map/tours/${tourId}`, { headers });
                if (!res.ok) {
                    const msg = await res.text();
                    throw new Error(msg || `Failed to load tour (${res.status})`);
                }
                const data = await res.json();
                if (!isCancelled) setTourData(data);
            } catch (e) {
                if (!isCancelled) setTourError(String(e?.message || e));
            } finally {
                if (!isCancelled) setTourLoading(false);
            }
        };
        load();
        return () => { isCancelled = true; };
    }, [tourId, accessToken]);

    const stops = useMemo(() => {
        const list = tourData?.tracks || [];
        return [...list].sort((a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0));
    }, [tourData]);

    const startStop = useCallback((trackId) => {
        if (!trackId) return;
        dispatch(setCurrentTour({ tourId, tourTitle: tourData?.tourTitle || tour?.title || '' }));
        dispatch(loadTourTracks(
            stops.map((s) => ({
                trackId: s.trackId,
                title: s.title,
                tourTitle: tourData?.tourTitle || tour?.title || '',
            }))
        ));
        dispatch(playTrackRequest(trackId, tourId));
    }, [dispatch, stops, tourId, tourData?.tourTitle, tour?.title]);

    const imageUrls = tour?.image_urls ? tour.image_urls.split(",").filter(url => url.trim()) : [];
    const [activeIndex, setActiveIndex] = React.useState(0);

    const updateActiveIndex = (increment) => {
        if (imageUrls.length === 0) return;
        const maxIndex = imageUrls.length - 1;
        let newIndex = activeIndex + increment;
        if (newIndex < 0) newIndex = maxIndex;
        else if (newIndex > maxIndex) newIndex = 0;
        setActiveIndex(newIndex);
    };

    return (
        <Layout selected="tour">
            {tour && (
                <div className="tour-page">
                    <nav aria-label="breadcrumb" className="breadcrumb-nav">
                        <ol className="breadcrumb">
                            <li className="breadcrumb-item">
                                <Link to="/">Home</Link>
                            </li>
                            <li className="breadcrumb-item active">{tour.title}</li>
                        </ol>
                    </nav>

                    <div className="tour-layout">
                        <div className="tour-main">
                            {imageUrls.length > 0 && (
                                <div className="tour-gallery mb-4">
                                    <div className="carousel slide">
                                        <div className="carousel-inner rounded">
                                            {imageUrls.map((imageUrl, index) => (
                                                <div 
                                                    className={index === activeIndex ? "carousel-item active" : "carousel-item"} 
                                                    key={index}
                                                >
                                                    <img 
                                                        src={imageUrl} 
                                                        className="d-block w-100"
                                                        style={{ maxHeight: '450px', objectFit: 'cover' }}
                                                        alt={`${tour.title} - ${index + 1}`} 
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                        {imageUrls.length > 1 && (
                                            <>
                                                <button className="carousel-control-prev" type="button" onClick={() => updateActiveIndex(-1)}>
                                                    <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                                                </button>
                                                <button className="carousel-control-next" type="button" onClick={() => updateActiveIndex(1)}>
                                                    <span className="carousel-control-next-icon" aria-hidden="true"></span>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            <h1 className="tour-title">{tour.title}</h1>
                            
                            <div className="tour-meta">
                                <span className="meta-item">
                                    <i className="bi bi-clock"></i> {tour.duration_in_hours} hours
                                </span>
                            </div>

                            <div className="tour-description">
                                <h3>About This Tour</h3>
                                <p>{tour.description}</p>
                            </div>

                            <div className="tour-description">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h3 className="mb-0">Stops</h3>
                                    <button
                                        className="btn btn-outline-dark btn-sm"
                                        onClick={() => startStop(stops?.[0]?.trackId)}
                                        disabled={tourLoading || !stops?.length}
                                    >
                                        Play from start
                                    </button>
                                </div>
                                {tourLoading ? (
                                    <p className="text-muted mt-2 mb-0">Loading stops…</p>
                                ) : tourError ? (
                                    <p className="text-danger mt-2 mb-0">{tourError}</p>
                                ) : stops.length === 0 ? (
                                    <p className="text-muted mt-2 mb-0">No stops yet.</p>
                                ) : (
                                    <div className="list-group mt-3">
                                        {stops.map((s, idx) => (
                                            <button
                                                key={s.trackId}
                                                type="button"
                                                className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                                onClick={() => startStop(s.trackId)}
                                            >
                                                <div className="text-start">
                                                    <div className="fw-semibold">{idx + 1}. {s.title}</div>
                                                    <div className="text-muted small">
                                                        {s.lat?.toFixed ? `${s.lat.toFixed(4)}, ${s.lng.toFixed(4)}` : ''}
                                                    </div>
                                                </div>
                                                <span className="text-muted small">Play</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {tour.tourguide && (
                                <div className="host-section">
                                    <h3>Guide</h3>
                                    <div className="host-card">
                                        {tour.tourguide.avatar_url && (
                                            <img 
                                                src={tour.tourguide.avatar_url} 
                                                alt={tour.tourguide.name}
                                                className="host-avatar"
                                            />
                                        )}
                                        <div className="host-info">
                                            <h4>{tour.tourguide.name}</h4>
                                            {tour.tourguide.bio && (
                                                <p className="text-muted">{tour.tourguide.bio}</p>
                                            )}
                                            <div className="host-contact">
                                                {tour.tourguide.email && (
                                                    <a href={`mailto:${tour.tourguide.email}`}>
                                                        <i className="bi bi-envelope"></i>
                                                    </a>
                                                )}
                                                {tour.tourguide.facebook && (
                                                    <a href={tour.tourguide.facebook} target="_blank" rel="noopener noreferrer">
                                                        <i className="bi bi-facebook"></i>
                                                    </a>
                                                )}
                                                {tour.tourguide.instagram && (
                                                    <a href={tour.tourguide.instagram} target="_blank" rel="noopener noreferrer">
                                                        <i className="bi bi-instagram"></i>
                                                    </a>
                                                )}
                                                {tour.tourguide.webpage && (
                                                    <a href={tour.tourguide.webpage} target="_blank" rel="noopener noreferrer">
                                                        <i className="bi bi-globe"></i>
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="tour-sidebar" />
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default Tour;
